import { readContract, signTypedData } from '@wagmi/core';
import { useSwitchChain } from '@wagmi/vue';
import axios from 'axios';
import { type Address, type Hash, type Hex, parseUnits } from 'viem';

import {
    computed,
    EvmItemType,
    type EvmOrderData,
    useNuxtApp,
    useProviderConfig,
    useState,
    useTransactionStore
} from '#imports';
import { useAlertsStore } from '~/stores/alerts/useAlertsStore';
import { useUserStore } from '~/stores/user/useUserStore';
import { useEventModalsStore } from '~/stores/eventModals/useEventModalsStore';
import type { EvmOrderParameters } from '~/service/api/types';
import { AsyncState } from '~/types/enums';

type MakeOfferType = {
    paramNetwork: User['network'];
    itemType: AppWTFNft['itemType'];
    erc20TokenAddress: Address;
    nftItemType: EvmItemType;
    value: number;
    endTime: string;
    amount?: number;
    marketplaceContract: ContractSettings;
    marketplace?: AppMarketplaceType;
    identifier: string;
    contract: Address;
    uuid: AppWTFNft['uuid'];
    fees: AppWTFCollectionFees;
};

const initialLoadingState = {
    status: AsyncState.IDLE,
    uuid: null
};

export function useMakeOffer(paramUuid?: string) {
    const { switchChainAsync } = useSwitchChain();
    const { addTransaction } = useTransactionStore();
    const evmCore = useEVMCore();
    const {
        map: {
            chainMap,
            chainMapById
        }
    } = useProviderConfig()(['contract', 'map']);

    const { $provider } = useNuxtApp();

    const wrapperService = evmCore.createTokenWrapper({
        switchNetworkFn: switchChainAsync,
        ...$provider
    });

    const makeOfferService = evmCore.createEvmOffer({
        signTypedData,
        chainMapById,
        ...$provider
    });

    const userStore = useUserStore();

    const modalStore = useEventModalsStore();

    const alertStore = useAlertsStore();
    const { offer } = useEVMApi();

    const loadingState = useState<{
        status: AsyncState;
        uuid: Nullable<AppWTFNft['uuid']>;
    }>(() => initialLoadingState);

    const resetLoadingState = (status?: AsyncState) => {
        if (status) loadingState.value.status = status;
        const resetLoadingState = setTimeout(
            () => loadingState.value = {
                status: AsyncState.IDLE,
                uuid: null
            },
            1000
        );
        clearTimeout(resetLoadingState);
    };

    const sendOffer = async ({
        marketplace,
        order,
        signature,
        paramNetwork,
        bigintValue,
        marketplaceContract
    }: {
        paramNetwork: User['network'];
        signature: Hex;
        order: EvmOrderParameters;
        marketplace?: AppMarketplaceType;
        marketplaceContract: ContractSettings;
        bigintValue: bigint;
    }) => {
        if (!userStore.user || !userStore.chainId) {
            throw new Error('User is not logged in');
        }
        if (marketplace === 'opensea') {
            const chainName = paramNetwork === 'eth'
                ? 'ethereum'
                : 'matic';

            try {
                await axios.post(
                    `https://api.opensea.io/api/v2/orders/${chainName}/seaport/offers`,
                    {
                        parameters: order,
                        protocol_address: '0x0000000000000068F116a894984e2DB1123eB395', // opensea address,
                        signature
                    },
                    {
                        headers: {
                            'X-Api-Key': '27f180cb62d64ab2abf6541790a08594',
                            'Accept': 'application/json'
                        }
                    }
                );
            }
            catch (err) {
                throw new Error('Server error when creating an offer');
            }
        }
        else {
            const erc20TokenAddress = order.consideration
                .find(considerationItem =>
                    considerationItem.itemType === EvmItemType.ERC20
                )?.token;

            await makeOfferService.eventEmitter.emit('balance', async () => {
                if (!userStore.user || !userStore.chainId) {
                    userStore.showUserIsDisconnect();
                    throw new Error('User is not connected');
                }

                const balance = await makeOfferService.getUserBalance({
                    chainId: userStore.chainId,
                    userAddress: userStore.user,
                    erc20TokenAddress
                });

                const { $evmCore } = useNuxtApp();

                const { map: { chainCurrencyMap } } = $evmCore.configs.getConfigFields('evm', (['map']));

                if (Number(balance) < bigintValue) {
                    throw new Error(JSON.stringify({
                        myBalance: balance.toString(),
                        needSum: bigintValue.toString(),
                        network: chainCurrencyMap[userStore.chainId]
                    }));
                }
                return balance;
            });

            try {
                await offer.fillOffer({
                    parameters: order,
                    protocolAddress: marketplaceContract.address,
                    signature,
                    network: paramNetwork,
                    user: userStore.user
                });
            }
            catch (e) {
                throw new Error('Server error when creating an offer');
            }
        }
    };

    const makeOffer = async ({
        paramNetwork,
        value,
        marketplace,
        marketplaceContract,
        contract,
        identifier,
        uuid,
        fees,
        itemType,
        nftItemType,
        endTime,
        amount,
        erc20TokenAddress
    }: MakeOfferType) => {
        if (userStore.user && userStore.chainId && marketplace && chainMap[paramNetwork]) {
            loadingState.value = {
                status: AsyncState.IS_LOADING,
                uuid
            };

            if (!chainMap[paramNetwork]) {
                throw new Error('Unsupported network');
            }

            if (userStore.chainId !== chainMap[paramNetwork].id) {
                try {
                    await switchChainAsync({ chainId: chainMap[paramNetwork].id });
                }
                catch (e) {
                    throw new Error('Network change error');
                }
            }

            try {
                const decimals = await readContract($provider.config, {
                    address: erc20TokenAddress,
                    chainId: chainMap[paramNetwork].id,
                    functionName: 'decimals',
                    abi: erc20Abi
                });

                const bigintValue = parseUnits(
                    String(value),
                    decimals
                );

                const { signature, order } = await makeOfferService.eventEmitter.emit('buildOffer', async () => {
                    if (userStore.chainId && userStore.user) {
                        await wrapperService.approveWrappedToken({
                            value: bigintValue,
                            marketplaceContractAddress: marketplaceContract.address,
                            currentChainId: userStore.chainId,
                            paramChainId: chainMap[paramNetwork].id,
                            erc20TokenAddress,
                            userAddress: userStore.user
                        });
                    }
                    else {
                        userStore.showUserIsDisconnect();
                    }

                    return await makeOfferService.buildOffer({
                        paramChainId: chainMap[paramNetwork].id,
                        erc20TokenAddress,
                        value,
                        marketplaceContract,
                        contract,
                        nftItemType,
                        identifier,
                        userAddress: userStore.user,
                        fees,
                        itemType,
                        endTime,
                        amount
                    });
                }, {
                    isApprove: true
                });

                await sendOffer({
                    paramNetwork,
                    signature,
                    marketplace,
                    order,
                    bigintValue,
                    marketplaceContract
                });

                resetLoadingState(AsyncState.IS_SUCCESS);
                modalStore.removeByType('approve');
                // alertStore.add({
                //     title: 'Success!',
                //     description: 'Your offer has been successfully submitted',
                //     critical: false,
                //     status: 'type.success'
                // });
            }
            catch (err) {
                const error = err as Error;
                resetLoadingState(AsyncState.IS_ERROR);
                return alertStore.add({
                    title: 'Oops, something went wrong',
                    description: `${error.message}`,
                    critical: false,
                    status: 'type.error'
                });
            }
        }
        else {
            userStore.showUserIsDisconnect();
        }
    };

    const acceptOffer = async ({ order, network, orderHash }: {
        order: EvmOrderData;
        orderHash: Hash;
        network: EvmNetwork;
    }) => {
        if (userStore.user) {
            loadingState.value = {
                status: AsyncState.IS_LOADING,
                uuid: orderHash
            };

            const paramChainId = chainMap[network].id;

            try {
                const transaction = await makeOfferService.eventEmitter.emit('acceptOffer', async () => {
                    if (!userStore.user) {
                        userStore.showUserIsDisconnect();
                        throw new Error('User is not connected');
                    }

                    return await makeOfferService.acceptOffer({
                        order,
                        paramChainId,
                        userAddress: userStore.user
                    });
                });

                if (transaction) {
                    addTransaction({
                        hash: transaction,
                        chainId: paramChainId,
                        tokenList: [],
                        value: 1,
                        currency: network,
                        status: 'pending',
                        type: 'buyNow',
                        typeTransaction: 'offer'
                    });
                }

                resetLoadingState(AsyncState.IS_SUCCESS);

                alertStore.add({
                    title: 'Transaction submitted',
                    description: 'Waiting for confirmation...',
                    critical: false,
                    status: 'type.info'
                });
            }
            // catch (err) {
            //     const error = err as Error;
            //     resetLoadingState(AsyncState.IS_ERROR);
            //     return alertStore.add({
            //         title: 'Oops, something went wrong',
            //         description: `${error.message}`,
            //         critical: false,
            //         status: 'type.error'
            //     });
            // }
            catch (err) {
                const error = err as Error;
                error.message = `${error.message}|orderHash:${orderHash}`; // Добавляем хэш в ошибку
                throw error;
            }
        }
        else {
            userStore.showUserIsDisconnect();
        }
    };

    return {
        makeOffer,
        loadingState,
        acceptOffer,
        isError: computed(() => !!paramUuid && loadingState.value.uuid === paramUuid && loadingState.value.status === AsyncState.IS_ERROR),
        isSuccess: computed(() => !!paramUuid && loadingState.value.uuid === paramUuid && loadingState.value.status === AsyncState.IS_SUCCESS),
        isLoading: computed(() => !!paramUuid && loadingState.value.uuid === paramUuid && loadingState.value.status === AsyncState.IS_LOADING)
    };
}
